/* eslint-disable @animech/axiom/no-references-in-top-level-values */
/* eslint-disable @animech/axiom/strict-function-syntax */
/* eslint-disable @animech/axiom/matching-rule-dependencies -- chair[] indexed deps verified by behavioral tests */
import { StandardRuleApi, RuleFactory } from "@animech/axiom-format";
import { Material } from "@animech/axiom-format/src/definitions/definition-bindings";
import { Root, root } from "./property-definitions";
import {
	chairOptions,
	defaultChair,
	getAllowedSeatingForLongSide,
	getMaxPopulation,
	maxLongSide,
	maxRoundChairs,
	TOTAL_CHAIR_SLOTS,
} from "./chair-data";
import { getActiveSides, tableAccessor } from "./side-accessors";
import {
	ALL_SIDES,
	ChairSlot,
	ChairType,
	chairTypeUniverse,
	defaultStockRef,
	defaultThemeForSeating,
	defaultThemeForTable,
	getFilteredDomains,
	getCompatibleThemes,
	getValidThemes,
	isComboExcluded,
	isProductInStock,
	isRoundTable,
	isThemeInStock,
	isThemeValidForProduct,
	MaterialOptions,
	materialThemes,
	MaterialTheme,
	projectThemeToProduct,
	resolveProjectedMetal,
	RoundTableType,
	SeatingType,
	Side,
	SIDE_COUNT,
	SIDE_INDICES,
	StockRef,
	ViewerMode,
} from "./type-definitions";
import { materials } from "src/definitions/scene-definitions";
import { tableOptions } from "src/definitions/table-data";
// eslint-disable-next-line @animech/axiom/no-unrelated-imports -- pure math functions, no scene/PlayCanvas deps
import {
	buildRectChairs,
	buildRoundChairs,
	type ChairConfig,
} from "src/scene/chair-layout/algorithms";

function isBlackMetal(metal: Material): boolean {
	return (
		metal === ("PaintMetalBlack" as Material) ||
		metal === ("PaintMetalPowdercoatBlack" as Material)
	);
}

function makeRules(
	rules: RuleFactory<Root, RuleApiExtensions>,
): RuleFactory<Root, RuleApiExtensions> {
	return rules;
}

export function query<TRoot>(_ruleApi: StandardRuleApi<TRoot>) {
	return <TTuple extends unknown[], TElement extends TTuple[number]>(
		array: [...TTuple][],
		callback: (...args: [...TTuple]) => TElement[],
	): TElement[] => {
		return array.flatMap((tuple) => callback(...tuple));
	};
}

export type RuleApiExtensions = {
	query: ReturnType<typeof query>;
};

function createTableMaterialRules({
	get,
	set,
	changed,
}: Pick<StandardRuleApi<Root>, "get" | "set" | "changed">) {
	const t = tableAccessor;
	return {
		tableMaterialDomain: {
			dependencies: [
				root.tableType,
				t.materialWood,
				t.materialMetal,
				t.theme,
				root.viewerMode,
			],
			run() {
				// Viewer mode owns table material values directly via
				// setup-product; skip the cross-filter narrowing so SKUs that
				// sit at intersection points (e.g. Pine+Green is the only
				// valid pair for either Pine or Green) can land via atomic
				// {value, domain} writes without the rule re-narrowing them.
				if (get(root.viewerMode).value !== "off") {
					return;
				}
				const tableType = get(root.tableType).value;
				const opts = tableOptions[tableType];
				let wood = get(t.materialWood).value;
				let metal = get(t.materialMetal).value;

				const theme = get(t.theme).value as MaterialTheme;
				if (changed(t.theme) && theme !== "theme-none") {
					const def = materialThemes[theme];
					if (!isComboExcluded(opts, def.wood, def.metal)) {
						if (def.wood && opts.wood.includes(def.wood)) {
							wood = def.wood;
						}
						if (opts.metal.includes(def.metal)) {
							metal = def.metal;
						}
					}
				}

				const filtered = getFilteredDomains(opts, wood, metal);
				set(t.materialWood, { domain: filtered.wood });
				set(t.materialMetal, { domain: filtered.metal });
				if (wood !== get(t.materialWood).value) {
					set(t.materialWood, wood);
				}
				if (metal !== get(t.materialMetal).value) {
					set(t.materialMetal, metal);
				}
			},
		},
		tableMaterialMetal: {
			dependencies: [t.materialMetal],
			run() {
				const metal = get(t.materialMetal).value;
				if (materials.includes(`${metal}-Weld` as Material)) {
					set(t.materialWeld, `${metal}-Weld` as Material);
				}
				const black = isBlackMetal(metal);
				set(
					t.materialScrewAtlas,
					(black ? "AtlasScrews-Black" : "AtlasScrews") as Material,
				);
				set(
					t.materialScrewPhillips,
					(black
						? "ScrewRoundedPhillips-M3x10mm-A-Black"
						: "ScrewRoundedPhillips-M3x10mm-A") as Material,
				);
			},
		},
		tableMaterialWood: {
			dependencies: [t.materialWood],
			run() {
				const wood = get(t.materialWood).value;
				if (materials.includes(`${wood}-Endcap` as Material)) {
					set(t.materialEndcap, `${wood}-Endcap` as Material);
				} else {
					set(t.materialEndcap, wood);
				}
			},
		},
		tableMaterialWood2: {
			dependencies: [root.tableType, t.materialWood],
			run() {
				const table = get(root.tableType).value;
				if (!table.startsWith("Table-HighTech")) {
					set(t.materialWood2, undefined);
					return;
				}
				const wood = get(t.materialWood).value;
				if (wood === "LaminateWhite") {
					set(t.materialWood2, "LaminateBlack" as Material);
				} else {
					set(t.materialWood2, wood);
				}
			},
		},
	};
}

// ---------------------------------------------------------------------------
// Theme domain rule factories
// ---------------------------------------------------------------------------

function createTableThemeDomainRule(
	{ get, set, changed }: Pick<StandardRuleApi<Root>, "get" | "set" | "changed">,
	stockRef: StockRef,
) {
	const t = tableAccessor;
	return {
		tableThemeDomain: {
			dependencies: [
				root.tableType,
				t.theme,
				root.materialTheme,
				root.stockVersion,
			],
			run() {
				const table = get(root.tableType).value;
				const opts = tableOptions[table];
				// Domain unfiltered — OOS themes stay valid. UI hides them.
				const domain = getValidThemes([opts]);
				set(t.theme, { domain });

				// Propagate master theme when it changes (replaces materialThemePropagation)
				if (changed(root.materialTheme)) {
					const masterTheme = get(root.materialTheme).value as MaterialTheme;
					if (masterTheme !== "theme-none") {
						const def = materialThemes[masterTheme];
						if (
							isThemeValidForProduct(def, opts) &&
							isThemeInStock(masterTheme, table, stockRef)
						) {
							set(t.theme, masterTheme);
							return;
						}
					}
				}

				if (!domain.includes(get(t.theme).value as MaterialTheme)) {
					const fallback = defaultThemeForTable[table];
					const themeToSet = domain.includes(fallback)
						? fallback
						: domain.length > 1
							? domain[1]
							: ("theme-none" as MaterialTheme);
					set(t.theme, themeToSet);
				}
			},
		},
	};
}

export function createRules(
	stockRef: StockRef = defaultStockRef,
): RuleFactory<Root, RuleApiExtensions> {
	return makeRules(({ get, set, changed }) => ({
		// --- Chair configuration rule ---
		// Non-template rule that handles:
		// 1. Chair configuration: table-change defaults, chairType/count domains, clamping
		// 2. Per-side theme domain: propagation, fallback, theme material application
		// 3. Per-side wood + metal derivation from theme (writes chair[i].chairMaterialWood
		//    and chair[i].chairMaterialMetal per element)
		// 4. Chair[] rep building: writes computed values to chair[] representative slots
		// 5. Layout computation: builds worker slots with positions (replaces ChairLayoutManager)
		// 6. Master theme domain + divergence detection (absorbed from materialThemeDetectOverride)
		// 7. Viewer-mode early-return: when root.viewerMode !== "off", hides chair[1..N]
		//    (or chair[0..N] for single-table) so setup-product owns chair[0] alone.
		//
		// Single-writer ownership of chair[]: this rule writes everything except the
		// derived material fields (chairMaterialWeld, chairMaterialEndcap,
		// chairMaterialScrewAtlas, chairMaterialScrewPhillips), which are owned by the
		// chair.$ template rules chairMaterialMetalDerived and chairMaterialWoodDerived.
		// All chair[] writes are per-element — no whole-array set(root.chair, [...]) —
		// to coexist with the template rules per CLAUDE.md's whole-array-vs-template
		// conflict rule.
		//
		// materialThemeDetectOverride was absorbed because it cannot read from chair[]
		// (empty on init, stale across rules), but chairConfiguration has fresh data
		// in local variables.
		chairConfiguration: {
			dependencies: [
				root.tableType,
				root.materialTheme,
				root.tableTheme,
				root.chair,
				root.stockVersion,
				root.viewerMode,
				// Per-element deps: trigger re-evaluation when UI/tests set representative slot fields
				root.chair[0].chairType,
				root.chair[0].count,
				root.chair[0].chairTheme,
				root.chair[1].chairType,
				root.chair[1].count,
				root.chair[1].chairTheme,
				root.chair[2].chairType,
				root.chair[2].count,
				root.chair[2].chairTheme,
				root.chair[3].chairType,
				root.chair[3].count,
				root.chair[3].chairTheme,
				root.chair[4].chairType,
				root.chair[4].count,
				root.chair[4].chairTheme,
			],
			run() {
				const viewerMode = get(root.viewerMode).value as ViewerMode;
				if (viewerMode !== "off") {
					// Single-product viewer mode: setup-product owns chair[0] for
					// chair/bench/sofa modes; nothing in chair[] for single-table mode.
					// Hide all non-product slots and skip the table-arrangement layout.
					set(root.rectControlsVisible, false);
					set(root.roundControlsVisible, false);
					const startIdx = viewerMode === "single-table" ? 0 : 1;
					for (let i = startIdx; i < TOTAL_CHAIR_SLOTS; i++) {
						set(root.chair[i].visible, false);
						set(root.chair[i].count, 0);
					}
					return;
				}

				const table = get(root.tableType).value;
				const tableChanged = changed(root.tableType);
				const round = isRoundTable(table);

				set(root.rectControlsVisible, !round);
				set(root.roundControlsVisible, round);

				// Read current chair array (empty on first run)
				const chairs = get(root.chair).value as ChairSlot[];
				const hasChairs = chairs.length >= SIDE_COUNT;

				// --- Per-side chairType + count determination ---
				const allowedLongSide = round
					? []
					: getAllowedSeatingForLongSide(table);

				const sideChairTypes: SeatingType[] = [];
				const sideCounts: number[] = [];
				const sideChairTypeDomains: SeatingType[][] = [];
				const sideCountDomains: { min: number; max: number }[] = [];

				for (const side of ALL_SIDES) {
					const idx = SIDE_INDICES[side];

					// Compute chairType domain with stock filtering
					// If stock filtering empties the domain, keep unfiltered (graceful degradation)
					let unfilteredDomain: SeatingType[];
					if (round) {
						unfilteredDomain = [...chairTypeUniverse];
					} else if (side === "top" || side === "bottom") {
						unfilteredDomain = allowedLongSide;
					} else {
						unfilteredDomain = [...chairTypeUniverse];
					}
					// Domain is unfiltered — OOS items stay valid so saved
					// configs aren't force-clamped. UI hides OOS items.
					const chairTypeDomain = unfilteredDomain;

					// Determine chairType
					let chairType: SeatingType;
					if (tableChanged || !hasChairs) {
						const def = defaultChair[table];
						const inStockFallback = chairTypeDomain.find((t) =>
							isProductInStock(t, stockRef),
						);
						chairType = isProductInStock(def, stockRef)
							? def
							: inStockFallback ?? def;
					} else {
						chairType = chairs[idx].chairType;
					}

					// Clamp chairType to domain
					if (!chairTypeDomain.includes(chairType)) {
						const def = defaultChair[table];
						chairType = chairTypeDomain.includes(def)
							? def
							: chairTypeDomain[0] ?? def;
					}

					// Compute count domain
					let countDomain: { min: number; max: number };
					if (round) {
						if (side === "round") {
							const max: number =
								maxRoundChairs[table as RoundTableType]?.[
									chairType as ChairType
								] ?? 0;
							countDomain = { min: 0, max };
						} else {
							countDomain = { min: 0, max: 0 };
						}
					} else {
						if (side === "top" || side === "bottom") {
							const max: number = maxLongSide[table]?.[chairType] ?? 2;
							countDomain = { min: 0, max };
						} else if (side === "left" || side === "right") {
							countDomain = { min: 0, max: 1 };
						} else {
							countDomain = { min: 0, max: 0 };
						}
					}

					// Determine count
					let count: number;
					if (tableChanged || !hasChairs) {
						const pop = getMaxPopulation(table);
						const countMap: Record<Side, number> = {
							top: pop.topCount,
							bottom: pop.bottomCount,
							left: pop.leftCount,
							right: pop.rightCount,
							round: pop.chairCount,
						};
						count = countMap[side];
					} else {
						count = chairs[idx].count;
					}

					// Clamp count to domain
					if (count > countDomain.max) {
						count = countDomain.max;
					}

					sideChairTypes.push(chairType);
					sideCounts.push(count);
					sideChairTypeDomains.push(chairTypeDomain);
					sideCountDomains.push(countDomain);
				}

				// --- Per-side theme + material computation + representative slot building ---
				const slots: ChairSlot[] = [];
				const sideThemeDomains: MaterialTheme[][] = [];

				for (const side of ALL_SIDES) {
					const idx = SIDE_INDICES[side];
					const chairType = sideChairTypes[idx];
					const opts = chairOptions[chairType];

					// Theme domain (filtered by stock)
					// Domain unfiltered — OOS themes stay valid. UI hides them.
					const validThemes = getValidThemes([opts]);
					sideThemeDomains.push(validThemes);

					let themeValue: MaterialTheme;
					if (tableChanged || !hasChairs) {
						const defTheme = defaultThemeForSeating[chairType];
						if (isThemeInStock(defTheme, chairType, stockRef)) {
							themeValue = defTheme;
						} else {
							const inStockTheme = validThemes.find(
								(t) =>
									t !== "theme-none" && isThemeInStock(t, chairType, stockRef),
							);
							themeValue = inStockTheme ?? defTheme;
						}
					} else {
						themeValue = chairs[idx].chairTheme;
					}

					// Propagate master theme when it changes or when table switches
					if (changed(root.materialTheme) || tableChanged) {
						const masterTheme = get(root.materialTheme).value as MaterialTheme;
						if (masterTheme !== "theme-none") {
							const projected = projectThemeToProduct(masterTheme, opts);
							if (projected && isThemeInStock(projected, chairType, stockRef)) {
								themeValue = projected;
							}
						}
					}

					// Fallback if current theme not in valid domain
					if (!validThemes.includes(themeValue)) {
						const fallback = defaultThemeForSeating[chairType];
						themeValue = validThemes.includes(fallback)
							? fallback
							: validThemes.length > 1
								? validThemes[1]
								: ("theme-none" as MaterialTheme);
					}

					// --- Materials: derive from current theme ---
					const materialTheme =
						themeValue !== "theme-none"
							? themeValue
							: defaultThemeForSeating[chairType];
					const themeDef = materialThemes[materialTheme];
					let wood: Material | undefined = themeDef.wood;
					let metal: Material = themeDef.metal;

					// Check excluded combo — fall back to first valid options
					if (isComboExcluded(opts, wood, metal)) {
						wood = opts.wood.length > 0 ? opts.wood[0] : undefined;
						metal = opts.metal[0];
					} else {
						if (wood !== undefined && !opts.wood.includes(wood)) {
							wood = opts.wood.length > 0 ? opts.wood[0] : undefined;
						}
						if (!opts.metal.includes(metal)) {
							metal = opts.metal[0];
						}
					}

					// Domain filtering — clamp to valid cross-filtered domain
					const filtered = getFilteredDomains(opts, wood, metal);
					if (wood !== undefined && !filtered.wood.includes(wood)) {
						wood = filtered.wood.length > 0 ? filtered.wood[0] : undefined;
					}
					if (!filtered.metal.includes(metal)) {
						metal = filtered.metal[0];
					}

					// Derive weld + screws from metal
					let weld: Material = metal;
					if (materials.includes(`${metal}-Weld` as Material)) {
						weld = `${metal}-Weld` as Material;
					}
					const black = isBlackMetal(metal);
					const screwAtlas = (
						black ? "AtlasScrews-Black" : "AtlasScrews"
					) as Material;
					const screwPhillips = (
						black
							? "ScrewRoundedPhillips-M3x10mm-A-Black"
							: "ScrewRoundedPhillips-M3x10mm-A"
					) as Material;

					// Derive endcap from wood
					let endcap: Material | undefined = wood;
					if (
						wood !== undefined &&
						materials.includes(`${wood}-Endcap` as Material)
					) {
						endcap = `${wood}-Endcap` as Material;
					}

					// Build representative slot with computed materials
					slots.push({
						visible: false,
						pos: { x: 0, y: 0, z: 0 },
						rot: { x: 0, y: 0, z: 0 },
						chairType,
						chairMaterialWood: wood,
						chairMaterialMetal: metal,
						chairMaterialWeld: weld,
						chairMaterialEndcap: endcap,
						chairMaterialScrewAtlas: screwAtlas,
						chairMaterialScrewPhillips: screwPhillips,
						sideId: side,
						count: sideCounts[idx],
						chairTheme: themeValue,
					});
				}

				// --- Layout computation (replaces ChairLayoutManager) ---
				let workers: ChairSlot[];
				if (round) {
					const roundRep = slots[SIDE_INDICES.round];
					workers = buildRoundChairs(
						table,
						roundRep.chairType as ChairType,
						roundRep.count,
						roundRep.chairMaterialWood,
						roundRep.chairMaterialMetal,
						roundRep.chairMaterialWeld,
						roundRep.chairMaterialEndcap,
						roundRep.chairMaterialScrewAtlas,
						roundRep.chairMaterialScrewPhillips,
						roundRep.sideId,
						roundRep.chairTheme,
					);
				} else {
					const toConfig = (rep: ChairSlot): ChairConfig => ({
						chairType: rep.chairType,
						chairMaterialWood: rep.chairMaterialWood,
						chairMaterialMetal: rep.chairMaterialMetal,
						chairMaterialWeld: rep.chairMaterialWeld,
						chairMaterialEndcap: rep.chairMaterialEndcap,
						chairMaterialScrewAtlas: rep.chairMaterialScrewAtlas,
						chairMaterialScrewPhillips: rep.chairMaterialScrewPhillips,
						sideId: rep.sideId,
						chairTheme: rep.chairTheme,
					});
					workers = buildRectChairs(
						table,
						toConfig(slots[SIDE_INDICES.top]),
						toConfig(slots[SIDE_INDICES.bottom]),
						toConfig(slots[SIDE_INDICES.left]),
						toConfig(slots[SIDE_INDICES.right]),
						slots[SIDE_INDICES.top].count,
						slots[SIDE_INDICES.bottom].count,
						slots[SIDE_INDICES.left].count,
						slots[SIDE_INDICES.right].count,
					);
				}
				// Per-element writes that EXCLUDE derived fields (chairMaterialWeld,
				// chairMaterialEndcap, chairMaterialScrewAtlas, chairMaterialScrewPhillips).
				// Those are owned by the chair.$ template rules below — separating the
				// writers eliminates Axiom's whole-array-vs-template-rule conflict.
				const allSlots = [...slots, ...workers];
				for (let i = 0; i < allSlots.length; i++) {
					const s = allSlots[i];
					set(root.chair[i].visible, s.visible);
					set(root.chair[i].pos.x, s.pos.x);
					set(root.chair[i].pos.y, s.pos.y);
					set(root.chair[i].pos.z, s.pos.z);
					set(root.chair[i].rot.x, s.rot.x);
					set(root.chair[i].rot.y, s.rot.y);
					set(root.chair[i].rot.z, s.rot.z);
					set(root.chair[i].chairMaterialWood, s.chairMaterialWood);
					set(root.chair[i].chairMaterialMetal, s.chairMaterialMetal);
					set(root.chair[i].sideId, s.sideId);
					// Reps (0..SIDE_COUNT-1) get chairType/count/chairTheme written
					// WITH domain in the block below — skip plain-value writes to
					// avoid double-writes in the same rule cycle.
					if (i >= SIDE_COUNT) {
						set(root.chair[i].chairType, s.chairType);
						set(root.chair[i].count, s.count);
						set(root.chair[i].chairTheme, s.chairTheme);
					}
				}

				// --- Set per-element values + domains on representatives ---
				// Domain-only set() resets element values to $state defaults,
				// so we must include the value in every domain write.
				for (let i = 0; i < SIDE_COUNT; i++) {
					set(root.chair[i].chairType, {
						value: sideChairTypes[i],
						domain: sideChairTypeDomains[i],
					});
					set(root.chair[i].count, {
						value: sideCounts[i],
						domain: sideCountDomains[i],
					});
					set(root.chair[i].chairTheme, {
						value: slots[i].chairTheme,
						domain: sideThemeDomains[i],
					});
				}

				// --- Master theme domain + divergence detection ---
				// (absorbed from materialThemeDetectOverride — can't be a separate
				// rule because it needs fresh per-side data that only exists in
				// local variables here; get(root.chair) returns stale/empty data)
				const masterTheme = get(root.materialTheme).value as MaterialTheme;
				const none = "theme-none" as MaterialTheme;

				const activeSides = getActiveSides(table);
				const activeProducts: MaterialOptions[] = [tableOptions[table]];
				for (const side of activeSides) {
					const idx = SIDE_INDICES[side];
					if (sideCounts[idx] > 0) {
						activeProducts.push(chairOptions[sideChairTypes[idx]]);
					}
				}
				const compatibleThemes = getCompatibleThemes(activeProducts);
				// Filter master domain by stock for ALL active products
				// Domain unfiltered by stock — OOS themes stay valid. UI hides them.
				const masterDomain: MaterialTheme[] = [];
				for (const t of compatibleThemes) {
					if (t === "theme-none") {
						continue;
					}
					masterDomain.push(t);
				}
				let domainForSet: MaterialTheme[];
				if (masterDomain.length > 0) {
					domainForSet = masterDomain;
				} else {
					const tableFallback = getValidThemes([tableOptions[table]]);
					domainForSet = [];
					for (const t of tableFallback) {
						if (t !== "theme-none") {
							domainForSet.push(t);
						}
					}
				}

				// If master is outside intersection and table didn't change,
				// keep the current master value by adding it to the domain.
				// This prevents chair type changes from clamping all sides'
				// materials — the incompatible side diverges instead.
				// On table change, clamp to a compatible theme.
				if (!domainForSet.includes(masterTheme) && !tableChanged) {
					domainForSet = [...domainForSet, masterTheme];
				}
				set(root.materialTheme, { domain: domainForSet });

				if (!domainForSet.includes(masterTheme)) {
					// Table change evicted master — clamp to compatible theme
					const fallback = defaultThemeForTable[table];
					const themeToSet = domainForSet.includes(fallback)
						? fallback
						: domainForSet[0];
					set(root.materialTheme, themeToSet);
					set(root.materialThemeDiverged, false);
					return;
				}

				// Check per-product theme divergence from master
				let diverged = false;
				const masterDef = materialThemes[masterTheme];
				const tableTheme = get(root.tableTheme).value as MaterialTheme;
				if (tableTheme !== none) {
					const tableDef = materialThemes[tableTheme];
					const tableOpts = tableOptions[table];
					const resolvedTableMetal = resolveProjectedMetal(
						masterDef.metal,
						tableOpts,
					);
					if (tableDef.metal !== resolvedTableMetal) {
						diverged = true;
					} else if (
						tableOpts.wood.length > 0 &&
						tableDef.wood !== masterDef.wood
					) {
						diverged = true;
					}
				}
				if (!diverged) {
					for (let i = 0; i < SIDE_COUNT; i++) {
						const ppt = slots[i].chairTheme;
						if (ppt === none) {
							continue;
						}
						const pptDef = materialThemes[ppt];
						const opts = chairOptions[sideChairTypes[i]];
						const resolvedChairMetal = resolveProjectedMetal(
							masterDef.metal,
							opts,
						);
						if (pptDef.metal !== resolvedChairMetal) {
							diverged = true;
							break;
						}
						if (opts.wood.length > 0 && pptDef.wood !== masterDef.wood) {
							diverged = true;
							break;
						}
					}
				}
				set(root.materialThemeDiverged, diverged);
			},
		},
		...createTableMaterialRules({ get, set, changed }),

		// --- Chair material-derivation template rules ---
		// Run on every chair slot (including viewer-mode chair[0]). Own the
		// derived fields (weld, endcap, screws) so chairConfiguration's
		// per-element writes never conflict with them.
		chairMaterialMetalDerived: {
			dependencies: [root.chair.chairMaterialMetal],
			run() {
				const metal = get(root.chair.chairMaterialMetal).value;
				if (materials.includes(`${metal}-Weld` as Material)) {
					set(root.chair.chairMaterialWeld, `${metal}-Weld` as Material);
				} else {
					set(root.chair.chairMaterialWeld, metal);
				}
				const black = isBlackMetal(metal);
				set(
					root.chair.chairMaterialScrewAtlas,
					(black ? "AtlasScrews-Black" : "AtlasScrews") as Material,
				);
				set(
					root.chair.chairMaterialScrewPhillips,
					(black
						? "ScrewRoundedPhillips-M3x10mm-A-Black"
						: "ScrewRoundedPhillips-M3x10mm-A") as Material,
				);
			},
		},
		chairMaterialWoodDerived: {
			dependencies: [root.chair.chairMaterialWood],
			run() {
				const wood = get(root.chair.chairMaterialWood).value;
				if (
					wood !== undefined &&
					materials.includes(`${wood}-Endcap` as Material)
				) {
					set(root.chair.chairMaterialEndcap, `${wood}-Endcap` as Material);
				} else {
					set(root.chair.chairMaterialEndcap, wood);
				}
			},
		},

		// --- Viewer-mode visibility ---
		// Drives tableVisible from viewerMode. Single writer.
		viewerVisibility: {
			dependencies: [root.viewerMode],
			run() {
				const mode = get(root.viewerMode).value as ViewerMode;
				set(root.tableVisible, mode === "off" || mode === "single-table");
			},
		},

		// --- Theme domain rules ---
		...createTableThemeDomainRule({ get, set, changed }, stockRef),

		// --- Area dimension swap ---
		flipAreaDimensions: {
			dependencies: [root.flipArea, root.areaWidth, root.areaDepth],
			run() {
				if (!changed(root.flipArea) || !get(root.flipArea).value) {
					return;
				}
				const width = get(root.areaWidth).value;
				const depth = get(root.areaDepth).value;
				set(root.areaWidth, { value: depth });
				set(root.areaDepth, { value: width });
				set(root.flipArea, { value: false });
			},
		},
	}));
}

// eslint-disable-next-line @animech/axiom/valid-axiom-system-values -- rules created via factory for stock injection
export const rules = createRules();
